import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Paper,
    Chip,
    Stack,
    Divider,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle,
    Cancel,
    AccessTime,
    EmojiEvents,
    TrendingUp
} from '@mui/icons-material';
import { getTopicQuizAttempts, QuizAttemptResponseDTO } from '../../services/quizService';
import { format } from 'date-fns';

interface PastAttemptsDialogProps {
    open: boolean;
    onClose: () => void;
    topicId: number;
}

const PastAttemptsDialog: React.FC<PastAttemptsDialogProps> = ({ open, onClose, topicId }) => {
    const [attempts, setAttempts] = useState<QuizAttemptResponseDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            loadAttempts();
        }
    }, [open, topicId]);

    const loadAttempts = async () => {
        setLoading(true);
        setError(null);
        try {
            // Always use topic level to show all quiz attempts for the topic
            const data = await getTopicQuizAttempts(topicId);
            console.log('Loaded past attempts:', data);
            setAttempts(data || []);
        } catch (err: any) {
            console.error('Failed to load past attempts:', err);
            console.error('Error details:', {
                message: err?.message,
                response: err?.response?.data,
                status: err?.response?.status
            });
            setError(err?.response?.data?.message || err?.message || 'Failed to load past attempts');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
        } catch {
            return dateString;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                        Past Quiz Attempts
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : attempts.length === 0 ? (
                    <Alert severity="info">
                        No past attempts found. Complete a quiz to see your results here.
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Score</TableCell>
                                    <TableCell>Result</TableCell>
                                    <TableCell>Correct</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Time</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {attempts.map((attempt) => (
                                    <TableRow key={`${attempt.agentGenerated ? 'a' : 'q'}-${attempt.id}`} hover>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(attempt.completedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {typeof attempt.score === 'number' 
                                                    ? attempt.score.toFixed(1) 
                                                    : typeof attempt.score === 'object' && attempt.score !== null
                                                    ? (attempt.score as any).toFixed?.(1) || String(attempt.score)
                                                    : 'N/A'}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={attempt.passed ? <CheckCircle /> : <Cancel />}
                                                label={attempt.passed ? 'Passed' : 'Failed'}
                                                color={attempt.passed ? 'success' : 'error'}
                                                size="small"
                                            />
                                            {attempt.passThresholdPercent != null && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Pass: {attempt.passThresholdPercent}%
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {attempt.correctAnswers} / {attempt.totalQuestions}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={attempt.agentGenerated ? 'Agent' : 'App'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {attempt.timeTakenMinutes && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {attempt.timeTakenMinutes} min
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PastAttemptsDialog;
